import React from 'react';
import { Form, InputNumber, Switch, Input, Row, Col, Divider, Typography } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

const StyledCard = styled(motion.div)`
  background: var(--color-container-dark);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  margin-bottom: 16px;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  [data-theme="light"] & {
    background: var(--color-container-light);
  }
`;

const CardHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  background: linear-gradient(90deg, rgba(24, 144, 255, 0.05), rgba(24, 144, 255, 0));

  [data-theme="light"] & {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
`;

const CardBody = styled.div`
  padding: 16px;
`;

const FormItemLabel = styled(Text)`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      type: "spring", 
      stiffness: 300, 
      damping: 20,
      delay: 0.1
    }
  }
};

interface UserSettingsProps {
  form: any;
}

const UserSettings: React.FC<UserSettingsProps> = () => {
  const { t } = useTranslation();
  
  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={12}>
        <StyledCard variants={cardVariants} initial="hidden" animate="visible">
          <CardHeader>
            <UserOutlined style={{ fontSize: '18px', marginRight: '12px', color: '#1890ff' }} />
            <Title level={5} style={{ margin: 0 }}>{t('settings.userSettings.usernameSett')}</Title>
          </CardHeader>
          <CardBody>
            <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
              {t('settings.userSettings.description')}
            </Text>
            
            <div style={{ marginBottom: '16px' }}>
              <FormItemLabel>{t('settings.userSettings.minLength')}</FormItemLabel>
              <Form.Item
                name="minUsernameLength"
                rules={[{ required: true, message: 'Please enter minimum length' }]}
                style={{ marginBottom: '8px' }}
              >
                <InputNumber 
                  min={3} 
                  max={15} 
                  style={{ width: '100%' }}
                  placeholder="e.g. 4" 
                />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('settings.userSettings.characters')}
              </Text>
            </div>
            
            <div>
              <FormItemLabel>{t('settings.userSettings.maxLength')}</FormItemLabel>
              <Form.Item
                name="maxUsernameLength"
                rules={[{ required: true, message: 'Please enter maximum length' }]}
                style={{ marginBottom: '8px' }}
              >
                <InputNumber 
                  min={3} 
                  max={15} 
                  style={{ width: '100%' }}
                  placeholder="e.g. 12" 
                />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('settings.userSettings.characters')}
              </Text>
            </div>
          </CardBody>
        </StyledCard>
      </Col>
      
      <Col xs={24} md={12}>
        <StyledCard variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
          <CardHeader>
            <LockOutlined style={{ fontSize: '18px', marginRight: '12px', color: '#1890ff' }} />
            <Title level={5} style={{ margin: 0 }}>{t('settings.userSettings.passSettings')}</Title>
          </CardHeader>
          <CardBody>
            <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
              {t('settings.userSettings.description')}
            </Text>
            
            <div style={{ marginBottom: '16px' }}>
              <FormItemLabel>{t('settings.userSettings.minLength')}</FormItemLabel>
              <Form.Item
                name="minPasswordLength"
                rules={[{ required: true, message: 'Please enter minimum length' }]}
                style={{ marginBottom: '8px' }}
              >
                <InputNumber 
                  min={6} 
                  max={20} 
                  style={{ width: '100%' }} 
                  placeholder="e.g. 8"
                />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('settings.userSettings.characters')}
              </Text>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <FormItemLabel>{t('settings.userSettings.maxLength')}</FormItemLabel>
              <Form.Item
                name="maxPasswordLength"
                rules={[{ required: true, message: 'Please enter maximum length' }]}
                style={{ marginBottom: '8px' }}
              >
                <InputNumber 
                  min={6} 
                  max={20} 
                  style={{ width: '100%' }} 
                  placeholder="e.g. 16"
                />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('settings.userSettings.characters')}
              </Text>
            </div>
            
            <Divider style={{ margin: '8px 0 16px' }} />
            
            <Form.Item
              name="useDefaultPassword"
              valuePropName="checked"
              style={{ marginBottom: '8px' }}
            >
              <Switch />
            </Form.Item>
            <Text style={{ marginLeft: '8px', marginBottom: '16px', display: 'block' }}>
              {t('settings.userSettings.useDefault')}
            </Text>

            {/* Separate conditional rendering outside Form.Item */}
            <Form.Item noStyle dependencies={['useDefaultPassword']}>
              {({ getFieldValue }) => {
                const isEnabled = !!getFieldValue('useDefaultPassword');
                
                return isEnabled ? (
                  <div>
                    <FormItemLabel>{t('settings.userSettings.defaultPassword')}</FormItemLabel>
                    <Form.Item
                      name="defaultPassword"
                      rules={[{ required: isEnabled, message: 'Please enter default password' }]}
                      style={{ marginBottom: '8px' }}
                    >
                      <Input.Password placeholder="Enter default password" />
                    </Form.Item>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {t('settings.userSettings.description')}
                    </Text>
                  </div>
                ) : null;
              }}
            </Form.Item>
          </CardBody>
        </StyledCard>
      </Col>
    </Row>
  );
};

export default UserSettings;